/**
 * Páginas de conteúdo para a busca orgânica.
 *
 * A home disputa UMA intenção ("baixar XML de nota fiscal"). Quem procura
 * "download de XML em lote" ou "XML a partir do SPED" quer outra resposta, e o
 * Google só mostra a página que responde aquela pergunta específica. Cada guia
 * aqui é uma dessas respostas, com título, descrição e `canonical` próprios.
 *
 * Mesma regra da landing: só o que o produto FAZ hoje (a captura, ainda em
 * liberação, tem a situação mostrada a partir do servidor — ver `captura`). Nada de prometer 100%
 * das notas (a fonte às vezes não tem), suporte a CT-e/NFC-e (não buscamos) ou
 * preço escrito à mão (a tabela muda pelo `.env`; aqui só se aponta para ela).
 *
 * Este arquivo alimenta TRÊS lugares, que precisam andar juntos:
 *   - a página React (`paginas/Guia.tsx`);
 *   - o `prerenderizar.mjs`, que grava o HTML e o FAQ estruturado de cada rota;
 *   - a lista de rotas hidratadas do `main.tsx`.
 * O `sitemap.xml` e o `robots.txt` são estáticos: rota nova, ajuste os dois.
 */

export interface Secao {
  titulo: string;
  paragrafos: string[];
  lista?: string[];
}

export interface Pergunta {
  pergunta: string;
  resposta: string;
}

export interface Guia {
  rota: string;
  /** Arquivo gerado no `dist` (sem pasta: ver `prerenderizar.mjs`). */
  arquivo: string;
  /** `<title>`: termo de busca primeiro, marca no fim. */
  titulo: string;
  /** Meta description: é o texto do resultado, e decide o clique. */
  descricao: string;
  /** Rótulo curto, para links entre guias e migalhas. */
  rotulo: string;
  h1: string;
  chamada: string;
  secoes: Secao[];
  passos: Array<{ titulo: string; texto: string }>;
  perguntas: Pergunta[];
  /**
   * Guia de uma função cuja situação (em liberação / disponível) e preço vêm
   * do servidor: a página mostra os dois logo abaixo da chamada, e o texto
   * fixo não afirma nenhum deles.
   */
  captura?: boolean;
}

export const GUIAS: Guia[] = [
  {
    rota: '/baixar-xml-nfe-pela-chave-de-acesso',
    arquivo: 'baixar-xml-nfe-pela-chave-de-acesso.html',
    titulo: 'Como Baixar o XML da NF-e pela Chave de Acesso | baixaxml',
    descricao:
      'Baixe o XML de uma nota fiscal eletrônica só com a chave de acesso de 44 dígitos, sem certificado digital e sem instalar nada. Veja o passo a passo.',
    rotulo: 'XML pela chave de acesso',
    h1: 'Como baixar o XML da NF-e pela chave de acesso',
    chamada:
      'Com a chave de acesso de 44 dígitos você obtém o arquivo XML da nota fiscal eletrônica — o documento fiscal de verdade, não a representação impressa. Sem certificado digital do emitente ou do destinatário, sem procuração e sem instalar programa.',
    secoes: [
      {
        titulo: 'O que é a chave de acesso',
        paragrafos: [
          'A chave de acesso é o número de 44 dígitos que identifica cada NF-e de forma única. Ela aparece no DANFE (a versão impressa da nota), logo abaixo do código de barras, e também dentro do próprio XML e nos arquivos do SPED.',
          'Os dígitos não são aleatórios: trazem a UF do emitente, o ano e mês de emissão, o CNPJ de quem emitiu, o modelo, a série, o número da nota e um dígito verificador. Por isso uma chave digitada com um número trocado simplesmente não encontra nota nenhuma — vale conferir antes de buscar.',
        ],
      },
      {
        titulo: 'Por que o XML, e não só o PDF do DANFE',
        paragrafos: [
          'O DANFE mostra a nota; o XML é a nota. Base de cálculo, alíquota, CST, CFOP e valores item a item só existem completos no arquivo XML, e é dele que sistemas contábeis e de apuração importam os dados.',
          'Além disso, a guarda exigida pela legislação é a do arquivo digital, pelo prazo decadencial de cinco anos. Ter só o PDF não cumpre essa obrigação.',
        ],
      },
      {
        titulo: 'Sem certificado digital',
        paragrafos: [
          'Os caminhos oficiais de download costumam exigir o certificado A1 ou A3 de uma das partes da nota. No baixaxml não pedimos certificado nem procuração eletrônica: você informa a chave de acesso e nós buscamos o arquivo.',
          'Isso resolve o caso mais comum no escritório contábil: o cliente mandou o PDF ou só a relação de notas, e o XML não chegou.',
        ],
      },
    ],
    passos: [
      {
        titulo: 'Crie sua conta',
        texto:
          'O cadastro é gratuito, sem mensalidade. Você confirma o e-mail e já pode buscar.',
      },
      {
        titulo: 'Informe a chave',
        texto:
          'Cole a chave de 44 dígitos — ou várias, uma por linha. Chaves repetidas são descartadas automaticamente.',
      },
      {
        titulo: 'Baixe o XML',
        texto:
          'Buscamos o arquivo e você baixa o resultado em ZIP. Se a nota não for encontrada na fonte, ela aparece separada e não é cobrada.',
      },
    ],
    perguntas: [
      {
        pergunta: 'Onde encontro a chave de acesso de uma nota fiscal?',
        resposta:
          'No DANFE, logo abaixo do código de barras, em blocos de quatro dígitos. Ela também está dentro do XML e nos registros de documentos do SPED (EFD ICMS/IPI e EFD Contribuições).',
      },
      {
        pergunta:
          'Preciso do certificado digital para baixar o XML pela chave?',
        resposta:
          'Não. Basta a chave de acesso. Não pedimos certificado A1 ou A3 nem procuração eletrônica.',
      },
      {
        pergunta: 'Toda chave de acesso retorna o XML?',
        resposta:
          'Nem sempre. Se a nota não estiver disponível na fonte — por exemplo, chave digitada errada — ela aparece como não encontrada no resultado, e não é cobrada.',
      },
      {
        pergunta: 'Posso baixar o XML de mais de uma nota de uma vez?',
        resposta:
          'Sim. Cole várias chaves, envie um CSV com as chaves em qualquer coluna, ou envie o arquivo SPED para extrairmos as chaves de todas as notas do período.',
      },
    ],
  },
  {
    rota: '/download-xml-nfe-em-lote',
    arquivo: 'download-xml-nfe-em-lote.html',
    titulo: 'Download de XML de NF-e em Lote | baixaxml',
    descricao:
      'Baixe XMLs de NF-e em lote a partir de uma lista de chaves ou do SPED. De dezenas a centenas de milhares de notas, com download em ZIP e busca que retoma sozinha.',
    rotulo: 'Download em lote',
    h1: 'Download de XML de NF-e em lote',
    chamada:
      'Fechamento com centenas de notas, cliente novo com o histórico do ano inteiro, auditoria de um período fechado: baixar XML nota a nota não escala. Em lote, você envia todas as chaves de uma vez e recebe os arquivos num ZIP.',
    secoes: [
      {
        titulo: 'De onde vêm as chaves',
        paragrafos: [
          'Você pode montar o lote de três formas, e misturar à vontade ao longo do mês:',
        ],
        lista: [
          'Colando as chaves direto na tela, uma por linha.',
          'Enviando um CSV ou planilha exportada do seu sistema: lemos as sequências de 44 dígitos de qualquer coluna e descartamos as repetidas.',
          'Enviando o SPED (EFD ICMS/IPI ou EFD Contribuições): extraímos as chaves de todas as notas escrituradas, inclusive de vários arquivos de uma vez em .zip.',
        ],
      },
      {
        titulo: 'Feito para volume',
        paragrafos: [
          'A busca roda em segundo plano, com nova tentativa automática nas falhas passageiras. Você pode fechar a aba: o progresso é gravado a cada nota concluída, e se o serviço reiniciar o lote continua de onde parou, sem recomeçar.',
          'Enquanto o lote anda, dá para baixar o parcial a qualquer momento. No fim, o resultado separa o que foi baixado, o que não foi encontrado na fonte e o que falhou — e só o que foi entregue é cobrado.',
        ],
      },
      {
        titulo: 'Preço por XML, que cai com o volume',
        paragrafos: [
          'Não há mensalidade nem fidelidade: você compra créditos por Pix e paga por XML entregue. O valor por nota diminui conforme o volume — o lote somado ao que a conta baixou nos últimos 30 dias —, e o custo total aparece antes de você confirmar a busca.',
          'Para automatizar o fechamento, há também uma API: emita uma chave nas configurações e dispare os lotes do seu próprio sistema.',
        ],
      },
    ],
    passos: [
      {
        titulo: 'Monte o lote',
        texto:
          'Cole as chaves, envie o CSV ou o SPED. Repetidas são descartadas automaticamente.',
      },
      {
        titulo: 'Confira o custo',
        texto:
          'Antes de começar você vê quantas notas entram e quanto o lote vai custar.',
      },
      {
        titulo: 'Baixe o ZIP',
        texto:
          'Acompanhe o progresso e baixe o resultado — parcial ou completo — em ZIP.',
      },
    ],
    perguntas: [
      {
        pergunta: 'Quantas notas posso baixar em um lote?',
        resposta:
          'De poucas dezenas a centenas de milhares. O lote é processado em segundo plano e o progresso fica gravado, então volumes grandes não precisam terminar de uma vez.',
      },
      {
        pergunta: 'Em que formato recebo os XMLs?',
        resposta:
          'Num arquivo ZIP com um XML por nota, nomeado pela chave de acesso. Dá para baixar o parcial enquanto o lote ainda está rodando.',
      },
      {
        pergunta: 'Pago pelas notas que não forem encontradas?',
        resposta:
          'Não. Notas não encontradas na fonte e falhas aparecem separadas no resultado e não são cobradas: você paga pelo XML entregue.',
      },
      {
        pergunta: 'Dá para integrar com o meu sistema?',
        resposta:
          'Sim. Emita uma chave de API nas configurações da conta e crie os lotes direto do seu sistema, sem abrir o painel.',
      },
    ],
  },
  {
    rota: '/baixar-xml-das-notas-do-sped',
    arquivo: 'baixar-xml-das-notas-do-sped.html',
    titulo: 'Baixar os XMLs das Notas do SPED (EFD) | baixaxml',
    descricao:
      'Envie o arquivo SPED e baixe os XMLs de todas as NF-e escrituradas. Extraímos as chaves da EFD ICMS/IPI e da EFD Contribuições, com filtros por CFOP, competência e mais.',
    rotulo: 'XML a partir do SPED',
    h1: 'Como baixar os XMLs das notas a partir do SPED',
    chamada:
      'O SPED que o escritório já entrega ao Fisco tem a chave de acesso de cada nota escriturada. Envie o arquivo e nós extraímos as chaves, mostramos o que há no período e buscamos os XMLs — sem montar planilha e sem pedir nota a nota ao cliente.',
    secoes: [
      {
        titulo: 'Quais arquivos SPED são aceitos',
        paragrafos: [
          'Lemos a EFD ICMS/IPI e a EFD Contribuições, em arquivo solto ou em .zip — inclusive a pasta do ano inteiro de uma vez. Notas que aparecem em mais de um arquivo são unificadas, e arquivos de ECD e ECF enviados junto são reconhecidos sem atrapalhar a análise.',
        ],
      },
      {
        titulo: 'Filtre antes de baixar',
        paragrafos: [
          'Depois da leitura, você escolhe exatamente o que quer baixar. Cada filtro mostra quantas notas traz antes de ser marcado, e o custo acompanha a seleção:',
        ],
        lista: [
          'Competência (mês de referência).',
          'CFOP.',
          'Entrada ou saída.',
          'Modelo e situação do documento.',
          'Participante (fornecedor ou cliente).',
        ],
      },
      {
        titulo: 'Para que serve no dia a dia do escritório',
        paragrafos: [
          'Conferência de créditos de ICMS, IPI, PIS e COFINS item a item, com a base de cálculo e o CST direto do XML. Cruzamento entre o escriturado e o que existe de arquivo: a nota que está no SPED e ninguém tinha o XML aparece na hora.',
          'Também resolve a entrada de cliente novo no meio do ano — suba os SPEDs anteriores e reconstitua o período — e a revisão de anos fechados para auditoria, recuperação de crédito ou due diligence.',
        ],
      },
    ],
    passos: [
      {
        titulo: 'Envie o SPED',
        texto:
          'EFD ICMS/IPI ou EFD Contribuições, um arquivo ou vários num .zip.',
      },
      {
        titulo: 'Escolha as notas',
        texto:
          'Filtre por competência, CFOP, entrada/saída, modelo, situação ou participante.',
      },
      {
        titulo: 'Baixe os XMLs',
        texto:
          'A busca roda em segundo plano e o resultado sai em ZIP, com parcial disponível durante o processo.',
      },
    ],
    perguntas: [
      {
        pergunta: 'É possível baixar o XML das notas a partir do arquivo SPED?',
        resposta:
          'Sim. O SPED traz a chave de acesso de cada nota escriturada. Extraímos essas chaves e buscamos o XML de cada uma.',
      },
      {
        pergunta: 'Quais tipos de SPED vocês leem?',
        resposta:
          'EFD ICMS/IPI e EFD Contribuições, em arquivo solto ou em .zip. Arquivos de ECD e ECF enviados junto são reconhecidos e não quebram a análise.',
      },
      {
        pergunta: 'Posso enviar o ano inteiro de uma vez?',
        resposta:
          'Sim. Envie a pasta do ano compactada em .zip. Notas repetidas entre os arquivos são unificadas antes da busca.',
      },
      {
        pergunta: 'Preciso baixar todas as notas do arquivo?',
        resposta:
          'Não. Depois da leitura você filtra por competência, CFOP, entrada ou saída, modelo, situação e participante, e só as notas selecionadas entram no lote.',
      },
    ],
  },
  {
    rota: '/baixar-xml-notas-de-entrada-com-certificado-a1',
    arquivo: 'baixar-xml-notas-de-entrada-com-certificado-a1.html',
    titulo:
      'Baixar XML das Notas de Entrada Automaticamente com Certificado A1 | baixaxml',
    descricao:
      'Receba o XML das NF-e emitidas contra o CNPJ direto da SEFAZ, com a Ciência da Operação registrada sozinha. Vários CNPJs numa conta, para escritórios contábeis.',
    rotulo: 'Notas de entrada com certificado A1',
    h1: 'Como receber o XML das notas de entrada automaticamente com o certificado A1',
    chamada:
      'Toda NF-e emitida contra um CNPJ fica disponível para ele na SEFAZ. Com o certificado A1 da empresa, dá para buscar essas notas sozinho, todos os dias — inclusive as que o fornecedor nunca mandou por e-mail. É o que a captura automática do baixaxml faz para cada empresa que o escritório cadastra.',
    captura: true,
    secoes: [
      {
        titulo: 'De onde vêm as notas',
        paragrafos: [
          'A SEFAZ mantém um serviço nacional de distribuição de documentos fiscais: quem é destinatário de uma NF-e pode consultá-lo com o próprio certificado digital e receber as notas emitidas contra o seu CNPJ, em ordem, uma vez cada.',
          'A consulta segue as regras da SEFAZ — intervalos mínimos entre consultas e sequência própria de cada CNPJ. Quem consulta fora delas é bloqueado por uma hora. O baixaxml cuida disso: guarda onde parou e respeita os intervalos, empresa por empresa.',
        ],
      },
      {
        titulo: 'Por que a Ciência da Operação importa',
        paragrafos: [
          'No primeiro momento a SEFAZ entrega só um resumo da nota: emitente, valor, data e chave. O XML completo, com os itens e os impostos, só é liberado depois que o destinatário se manifesta.',
          'A manifestação mais simples é a Ciência da Operação: ela só registra que a empresa tomou conhecimento da nota, sem confirmar nem recusar a operação. Precisa ser feita em até 10 dias da autorização. Com a opção ligada, o baixaxml envia a Ciência sozinho e, na consulta seguinte, recebe o XML completo.',
        ],
      },
      {
        titulo: 'O que o escritório recebe',
        paragrafos: [],
        lista: [
          'O XML completo de cada NF-e (modelo 55) emitida contra o CNPJ, guardado e pronto para baixar em ZIP.',
          'Vários CNPJs na mesma conta, cada um com a sua situação: ativa, aguardando consulta ou com problema no certificado.',
          'Cancelamentos que chegam como evento, com a nota marcada como cancelada.',
          'Aviso por e-mail antes de o certificado vencer, para a captura não parar sem ninguém perceber.',
        ],
      },
      {
        titulo: 'Limites que valem saber',
        paragrafos: [
          'A SEFAZ só disponibiliza as notas dos últimos 90 dias. Notas mais antigas não vêm pela captura — para elas, use o download pela chave de acesso ou pelo SPED, que continua funcionando do mesmo jeito.',
          'É preciso o certificado A1 (arquivo .pfx) da própria empresa, ou de outra do mesmo grupo, com a mesma raiz de CNPJ. O certificado A3, em cartão ou token, não serve para consulta automática. NFC-e e CT-e não entram: o serviço da SEFAZ distribui NF-e.',
        ],
      },
      {
        titulo: 'Segurança do certificado',
        paragrafos: [
          'O certificado é guardado cifrado e só é aberto, em memória, na hora de consultar a SEFAZ ou de enviar a Ciência. Ele não é usado para assinar mais nada, e você pode removê-lo a qualquer momento. Os detalhes estão na Política de Privacidade.',
        ],
      },
    ],
    passos: [
      {
        titulo: 'Envie o certificado A1',
        texto:
          'O arquivo .pfx e a senha. Conferimos a validade e o CNPJ na hora.',
      },
      {
        titulo: 'Cadastre as empresas',
        texto:
          'Cada CNPJ a monitorar, com a Ciência da Operação automática ligada ou não.',
      },
      {
        titulo: 'Baixe as notas',
        texto:
          'Elas chegam sozinhas. Filtre por empresa e período e baixe o ZIP.',
      },
    ],
    perguntas: [
      {
        pergunta: 'Preciso do certificado de cada empresa?',
        resposta:
          'Sim, de cada empresa ou de outra do mesmo grupo, com a mesma raiz de CNPJ. A SEFAZ só entrega as notas a quem prova ser o destinatário, e a prova é o certificado digital.',
      },
      {
        pergunta: 'A Ciência da Operação confirma que a mercadoria chegou?',
        resposta:
          'Não. A Ciência só registra que a empresa tomou conhecimento da nota. Confirmar a operação, dizer que ela não ocorreu ou que é desconhecida são outras manifestações, que continuam a cargo da empresa.',
      },
      {
        pergunta: 'Consigo as notas de anos anteriores?',
        resposta:
          'Pela captura, não: a SEFAZ só disponibiliza os últimos 90 dias. Para períodos mais antigos, envie o SPED ou as chaves de acesso e baixe os XMLs pelo download comum.',
      },
      {
        pergunta: 'Funciona com certificado A3?',
        resposta:
          'Não. O A3 fica num cartão ou token e precisa estar conectado a um computador. A consulta automática exige o A1, que é um arquivo.',
      },
    ],
  },
  {
    rota: '/xml-nfe-para-escritorios-contabeis',
    arquivo: 'xml-nfe-para-escritorios-contabeis.html',
    titulo: 'XML de NF-e para Escritórios Contábeis | baixaxml',
    descricao:
      'Pare de pedir XML ao cliente. Baixe os XMLs das notas de todas as empresas que o escritório atende pela chave de acesso ou pelo SPED, sem certificado digital.',
    rotulo: 'Para escritórios contábeis',
    h1: 'XML de NF-e para escritórios contábeis',
    chamada:
      'Todo fechamento começa igual: falta XML. O cliente mandou o PDF, esqueceu as notas de entrada, trocou de sistema. O baixaxml busca os arquivos pela chave de acesso ou direto do SPED, para o escritório parar de depender do que o cliente lembrou de enviar.',
    secoes: [
      {
        titulo: 'Sem certificado digital do cliente',
        paragrafos: [
          'Não é preciso ter o A1 de cada empresa instalado, guardar senha de certificado nem cadastrar procuração eletrônica. A chave de acesso basta — e ela já está no SPED que o escritório entrega.',
        ],
      },
      {
        titulo: 'Onde o XML faz falta',
        paragrafos: [],
        lista: [
          'Apuração e conferência de créditos de ICMS, IPI, PIS e COFINS, item a item.',
          'Importação no sistema contábil e fiscal sem redigitar nota.',
          'Guarda do arquivo digital pelo prazo decadencial de cinco anos.',
          'Cliente novo no meio do ano: reconstituir o histórico a partir dos SPEDs anteriores.',
          'Auditoria, revisão fiscal e recuperação de crédito de períodos fechados.',
        ],
      },
      {
        titulo: 'Uma conta para o escritório inteiro',
        paragrafos: [
          'Roda no navegador, sem instalar nada, e a equipe usa a mesma conta. Você compra créditos por Pix, sem mensalidade nem fidelidade, e paga por XML entregue — o valor por nota cai conforme o volume.',
          'As buscas e análises de cada conta pertencem só a ela, e para quem quer automatizar há uma API para disparar os lotes do próprio sistema do escritório.',
        ],
      },
    ],
    passos: [
      {
        titulo: 'Traga as chaves',
        texto: 'Do SPED do cliente, de uma planilha ou coladas na tela.',
      },
      {
        titulo: 'Escolha o que baixar',
        texto:
          'Filtre o período, o CFOP ou o participante e veja o custo antes de começar.',
      },
      {
        titulo: 'Receba os XMLs',
        texto: 'Tudo num ZIP, separado do que não foi encontrado na fonte.',
      },
    ],
    perguntas: [
      {
        pergunta: 'Preciso do certificado digital de cada cliente?',
        resposta:
          'Para baixar pela chave ou pelo SPED, não: basta a chave de acesso das notas — do SPED, de uma planilha ou digitada. O certificado A1 só entra se o escritório quiser a captura automática das notas de entrada, que é opcional.',
      },
      {
        pergunta: 'Tem mensalidade?',
        resposta:
          'Para baixar pela chave ou pelo SPED, não: você compra créditos por Pix quando precisar e paga por XML entregue, sem fidelidade. Só a captura automática pelo certificado A1, que é opcional, tem plano mensal, pela quantidade de CNPJs.',
      },
      {
        pergunta: 'Mais de uma pessoa do escritório pode usar?',
        resposta:
          'Sim. Roda no navegador, sem instalar nada, e a equipe pode usar a mesma conta.',
      },
      {
        pergunta: 'O baixaxml tem vínculo com a Receita ou a Sefaz?',
        resposta:
          'Não. É um serviço independente de consulta e download de documentos fiscais eletrônicos. Os XMLs pertencem ao contribuinte; apenas obtemos e entregamos os arquivos a quem informa a chave de acesso.',
      },
    ],
  },
];

export const ROTAS_GUIAS = GUIAS.map((g) => g.rota);
